import { useEffect, useState } from 'react';
import type { EventView, EntryView } from '@koto-event/shared';
import { Roulette } from '../components/Roulette.js';
import { Scratch } from '../components/Scratch.js';
import { ApiError } from '../lib/api.js';

interface Props {
  event: EventView;
  /** 抽選 API を呼ぶ。戻り値が当選結果 */
  onDraw: () => Promise<EntryView>;
  onComplete: (entry: EntryView) => void;
  onError: (message: string, authenticationExpired?: boolean) => void;
}

/**
 * 抽選画面（フロー図「抽選画面」）。
 *
 * 当選判定はサーバーで行い、演出はその結果を見せるだけ。
 * 画面を先に動かしてから結果を取りに行くと結果と演出がずれるため、
 * ここでは「先に API を呼び、結果を受け取ってから演出を回す」順序にしている。
 */
export function DrawScreen({ event, onDraw, onComplete, onError }: Props): JSX.Element {
  const [entry, setEntry] = useState<EntryView | null>(null);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await onDraw();
        if (cancelled) return;
        setEntry(result);
        setAnimating(true);
      } catch (e) {
        if (!cancelled) onError(e instanceof Error ? e.message : '抽選に失敗しました', e instanceof ApiError && e.status === 401);
      }
    })();
    return () => {
      cancelled = true;
    };
    // 初回マウント時のみ抽選する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const segments = event.prizes.map((p) => p.name);
  const resultIndex = entry?.prize ? event.prizes.findIndex((p) => p.id === entry.prize?.id) : null;

  const handleFinished = (): void => {
    if (entry) onComplete(entry);
  };

  return (
    <div className="card center">
      <h1>抽選中</h1>
      {event.lotteryType === 'SCRATCH' ? (
        <Scratch
          label={entry?.prize?.name ?? 'ざんねん…'}
          running={animating}
          onFinished={handleFinished}
        />
      ) : (
        <Roulette
          segments={segments}
          resultIndex={resultIndex !== null && resultIndex >= 0 ? resultIndex : null}
          running={animating}
          onFinished={handleFinished}
        />
      )}
      <p className="muted">{animating ? '結果が出るまでお待ちください' : '準備しています…'}</p>
    </div>
  );
}
